/**
 * 统一 HTTP 客户端：契约 §0 的响应信封 + §5.2 的错误码处理。
 *
 * 关键约定：
 * - **业务错误即异常**。信封里 `code !== 0` 一律抛 `ApiError`，调用方只处理
 *   异常，不必在每处判断 `code`。
 * - **全局副作用走事件总线**。40100/40102 清理会话、42900 弹额度、42901 起倒计时、
 *   502xx 提示"本次未消耗额度"——这些在 `handleBusinessError` 里统一 `emitApiEvent`，
 *   由 `ApiEvents.vue` 翻译成 UI。客户端本身不认识任何组件。
 * - **刷新令牌是单飞的**。refresh token 一次性轮换，N 个并发请求各自刷新会让
 *   后到的 N-1 个拿到 40102 并被登出；所以共享同一个 Promise。
 * - 本模块**不引入 Vue**：它要能在流式回调、Mock 以及 vitest（node 环境）里跑。
 */

import {
  ErrorCode,
  ERROR_MESSAGE,
  NON_BILLABLE_ERROR_CODES,
  RefreshResultSchema,
  isErrorCode,
  unwrapEnvelope,
  type ParseableSchema,
  type RefreshResult,
} from '@spirit/contracts';

import { emitApiEvent, type QuotaExceededDetails } from './api-events';
import { API_BASE_URL, isMockEnabled } from './env';
import { mockFetch } from './mock';
import { clearSession, getAccessToken, getRefreshToken, needsRefresh, setSession } from './token-store';

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export interface ApiRequestOptions {
  method?: HttpMethod;
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined | null>;
  /** `false` 表示匿名端点（登录、发验证码），不带 Authorization 头。 */
  auth?: boolean;
  signal?: AbortSignal;
  headers?: Record<string, string>;
}

/**
 * 传输层失败的哨兵码。
 *
 * 取负数是为了**永远不撞上**契约里的业务码（契约的正数码段已被占满，
 * 而 `code: 0` 表示成功）。
 */
export const NETWORK_ERROR_CODE = -1;

export interface ApiErrorInit {
  code: number;
  message: string;
  httpStatus: number;
  requestId: string;
  details: Record<string, unknown> | undefined;
  retryAfterSeconds?: number;
}

export class ApiError extends Error {
  readonly code: number;

  readonly httpStatus: number;

  readonly requestId: string;

  readonly details: Record<string, unknown> | undefined;

  /** 仅 42901 带此字段；来自 `Retry-After` 响应头。 */
  readonly retryAfterSeconds: number | undefined;

  constructor(init: ApiErrorInit) {
    super(init.message);
    this.name = 'ApiError';
    this.code = init.code;
    this.httpStatus = init.httpStatus;
    this.requestId = init.requestId;
    this.details = init.details;
    this.retryAfterSeconds = init.retryAfterSeconds;
  }

  /** 该错误是否不消耗额度（用于提示话术，与业务结算规则同源）。 */
  get notBillable(): boolean {
    return NON_BILLABLE_ERROR_CODES.includes(this.code);
  }

  /** 是否是"根本没连上后端"，而不是后端返回的错误。 */
  get isNetworkFailure(): boolean {
    return this.code === NETWORK_ERROR_CODE;
  }
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

/**
 * 是否是 abort 造成的失败。
 *
 * 只认 `name`，不认 `instanceof DOMException`：Mock 的假 SSE、node 环境的
 * undici、以及各浏览器对 abort 的包装类并不统一，但都遵守 `name === 'AbortError'`。
 */
export function isAbortError(error: unknown): boolean {
  return typeof error === 'object' && error !== null && (error as { name?: unknown }).name === 'AbortError';
}

/** 拼接完整 URL；`query` 里 `undefined|null|''` 的键直接不出现。 */
export function buildApiUrl(path: string, query?: ApiRequestOptions['query']): string {
  const base = API_BASE_URL.endsWith('/') ? API_BASE_URL.slice(0, -1) : API_BASE_URL;
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  const url = `${base}${normalizedPath}`;
  if (query === undefined) {
    return url;
  }
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '') {
      continue;
    }
    search.set(key, String(value));
  }
  const serialized = search.toString();
  return serialized.length === 0 ? url : `${url}?${serialized}`;
}

/** 构造请求头。令牌为空时**不加** `Authorization`，而不是加一个 `Bearer null`。 */
export function buildApiHeaders(init: { hasBody?: boolean; auth?: boolean } = {}): Record<string, string> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (init.hasBody === true) {
    headers['Content-Type'] = 'application/json';
  }
  if (init.auth !== false) {
    const token = getAccessToken();
    if (token !== null) {
      headers['Authorization'] = `Bearer ${token}`;
    }
  }
  return headers;
}

/**
 * 单飞刷新。
 *
 * `refreshInFlight` 是**模块级**的：只要还有一个刷新在飞，后来者都复用它。
 * 这与服务端"refresh token 一次性轮换"的语义严格配套——并发刷新必然把
 * 除第一个以外的所有请求变成 40102 + 强制登出。
 */
let refreshInFlight: Promise<string> | null = null;

export function refreshAccessToken(): Promise<string> {
  if (refreshInFlight === null) {
    refreshInFlight = performRefresh().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

/** 仅供测试：清掉可能在测试间泄漏的单飞 Promise。 */
export function resetRefreshStateForTest(): void {
  refreshInFlight = null;
}

/**
 * 刷新令牌。
 *
 * **故意绕过 `sendEnvelope`**：那条路径会在 40101 时再次调用刷新，形成无限递归。
 * 这里直接 `fetch`，并自己处理"刷新令牌本身失效"的终局（清会话 + 广播未授权）。
 */
async function performRefresh(): Promise<string> {
  const refreshToken = getRefreshToken();
  if (refreshToken === null) {
    const error = new ApiError({
      code: ErrorCode.UNAUTHORIZED,
      message: '登录状态已失效，请重新登录',
      httpStatus: 401,
      requestId: '',
      details: undefined,
    });
    clearSession();
    emitApiEvent({ type: 'unauthorized', message: error.message });
    throw error;
  }

  const response = await rawFetch(buildApiUrl('/auth/refresh'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ refreshToken }),
  });

  // 必须 `readJson` 之后再交给 `readEnvelope`：直接把 `Response` 递进去，
  // `readEnvelope` 会当成"没有 code 字段的对象"，于是每个刷新都被判失败、
  // 顺手 `clearSession()` —— 表现就是"刷新页面即掉登录"。
  const envelope = await readEnvelope(await readJson(response));
  if (!response.ok || envelope.code !== 0) {
    clearSession();
    throw handleBusinessError(envelope, response);
  }

  const result: RefreshResult | null = unwrapEnvelope(envelope, RefreshResultSchema);
  if (result === null) {
    clearSession();
    throw new ApiError({
      code: ErrorCode.INTERNAL,
      message: '刷新令牌响应结构与契约不一致',
      httpStatus: response.status,
      requestId: envelope.requestId,
      details: undefined,
    });
  }

  setSession(result);
  return result.accessToken;
}

interface RawEnvelope {
  code: number | null;
  message: string;
  data: unknown;
  requestId: string;
  details: Record<string, unknown> | undefined;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function readEnvelope(raw: unknown): RawEnvelope {
  const record = asRecord(raw);
  if (record === null) {
    return { code: null, message: '响应体不是 JSON 对象', data: null, requestId: '', details: undefined };
  }
  return {
    code: typeof record['code'] === 'number' ? record['code'] : null,
    message: typeof record['message'] === 'string' ? record['message'] : '',
    data: record['data'],
    requestId: typeof record['requestId'] === 'string' ? record['requestId'] : '',
    details: asRecord(record['details']) ?? undefined,
  };
}

/** 读 JSON；空 body 与解析失败都返回 null（由调用方决定怎么报错）。 */
async function readJson(response: Response): Promise<unknown> {
  try {
    const text = await response.text();
    if (text.trim().length === 0) {
      return null;
    }
    return JSON.parse(text) as unknown;
  } catch {
    return null;
  }
}

/**
 * 解析 `Retry-After`。
 *
 * 两种合法形式：秒数（`5`）或 HTTP 日期（`Wed, 21 Oct 2015 07:28:00 GMT`）。
 * 契约用的是秒数，但中间可能夹着网关，所以两种都认。
 */
function parseRetryAfter(header: string | null): number | undefined {
  if (header === null) {
    return undefined;
  }
  const trimmed = header.trim();
  if (/^\d+$/.test(trimmed)) {
    return Number.parseInt(trimmed, 10);
  }
  const timestamp = Date.parse(trimmed);
  if (Number.isNaN(timestamp)) {
    return undefined;
  }
  return Math.max(0, Math.ceil((timestamp - Date.now()) / 1000));
}

function messageOf(code: number, fallback: string): string {
  if (fallback.length > 0) {
    return fallback;
  }
  return isErrorCode(code) ? ERROR_MESSAGE[code] : '请求失败，请稍后重试';
}

/**
 * 解析 42900 的 `details`（契约 §5.2：`{scope, remainingPercent, resetAt}`）。
 *
 * 三个键全部给兜底、不用 `!` 断言：`details` 是附加信息，网关或旧服务端都可能给不全，
 * 而让错误处理路径自己抛 TypeError 会把「额度用完」变成一次白屏。
 *
 * ★ `scope` 兜底成 `daily` 是**最后手段而不是语义**：契约要求必带，服务端两个分支
 * （日/月）都显式传，只有不符合契约的服务端才会缺。它并不"更安全" —— `free` 档没有日上限，
 * 真有服务端漏传时，免费用户会看到「今日额度已用完」而实际拦他的是月度。
 * 真正的修法是服务端别漏传；这里只保证错误路径不白屏。
 *
 * `remainingPercent` 兜底成 0：42900 意味着"这一维已经过不去"，
 * 而 0 是唯一"宁可少报"的兜底值——填 100 会让弹窗说"还剩 100%"，比不显示更糟。
 */
function readQuotaDetails(details: Record<string, unknown> | undefined): QuotaExceededDetails {
  const rawResetAt = details?.['resetAt'];
  const rawPercent = details?.['remainingPercent'];
  const remainingPercent =
    typeof rawPercent === 'number' && Number.isFinite(rawPercent)
      ? Math.min(100, Math.max(0, Math.round(rawPercent)))
      : 0;
  return {
    scope: details?.['scope'] === 'monthly' ? 'monthly' : 'daily',
    remainingPercent,
    resetAt: typeof rawResetAt === 'string' ? rawResetAt : '',
  };
}

/**
 * 业务错误 → `ApiError`，**同时**广播全局事件。
 *
 * 广播与抛出同时发生是有意的：事件负责"界面上必须出现的反应"（弹窗、倒计时、登出），
 * 抛出负责"调用方必须停止当前流程"。只做其中一件都会留下缺口。
 */
function handleBusinessError(envelope: RawEnvelope, response: Response): ApiError {
  const retryAfterSeconds = parseRetryAfter(response.headers.get('retry-after'));
  const code = envelope.code ?? NETWORK_ERROR_CODE;
  const message = messageOf(code, envelope.message);

  switch (code) {
    case ErrorCode.UNAUTHORIZED:
    case ErrorCode.REFRESH_INVALID:
      clearSession();
      emitApiEvent({ type: 'unauthorized', message });
      break;
    case ErrorCode.QUOTA_EXCEEDED:
      emitApiEvent({ type: 'quota-exceeded', message, ...readQuotaDetails(envelope.details) });
      break;
    case ErrorCode.RATE_LIMITED:
      emitApiEvent({ type: 'rate-limited', message, retryAfterSeconds: retryAfterSeconds ?? 1 });
      break;
    case ErrorCode.UPSTREAM_ERROR:
    case ErrorCode.UPSTREAM_TIMEOUT:
      emitApiEvent({ type: 'upstream-failure', message, notBillable: true });
      break;
    case ErrorCode.MAINTENANCE:
      emitApiEvent({ type: 'maintenance', message });
      break;
    default:
      break;
  }

  return new ApiError({
    code,
    message,
    httpStatus: response.status,
    requestId: envelope.requestId,
    details: envelope.details,
    ...(retryAfterSeconds !== undefined ? { retryAfterSeconds } : {}),
  });
}

/** 真正出网的地方：Mock 模式短路到本地路由，否则走 fetch。 */
async function rawFetch(url: string, init: RequestInit): Promise<Response> {
  if (isMockEnabled()) {
    return mockFetch(url, init);
  }
  return fetch(url, init);
}

/** 网络层失败的统一话术：Mock 模式下"连不上"意味着 Mock 入口坏了，是另一种 bug。 */
function networkError(): ApiError {
  return new ApiError({
    code: NETWORK_ERROR_CODE,
    message: isMockEnabled()
      ? '本地 Mock 未响应（不应发生，请检查 mock 入口）'
      : '无法连接后端服务，请确认后端已启动或开启 Mock 模式',
    httpStatus: 0,
    requestId: '',
    details: undefined,
  });
}

async function sendEnvelope<T>(
  path: string,
  schema: ParseableSchema<T>,
  options: ApiRequestOptions,
  allowTokenRetry: boolean,
): Promise<T> {
  const method = options.method ?? 'GET';
  const auth = options.auth;

  // 预检刷新：本地已知 access token 过期/缺失但有 refresh token 时先换一张，
  // 省掉一次注定 40101 的往返。
  if (auth !== false && needsRefresh()) {
    await refreshAccessToken();
  }

  const hasBody = options.body !== undefined;
  const headers = { ...buildApiHeaders({ hasBody, ...(auth !== undefined ? { auth } : {}) }), ...options.headers };
  const url = buildApiUrl(path, options.query);

  let response: Response;
  try {
    response = await rawFetch(url, {
      method,
      headers,
      ...(hasBody ? { body: JSON.stringify(options.body) } : {}),
      ...(options.signal !== undefined ? { signal: options.signal } : {}),
    });
  } catch (error) {
    // abort 是调用方的主动决策，不是网络故障——必须原样抛出让上层识别。
    if (isAbortError(error)) {
      throw error;
    }
    throw networkError();
  }

  const envelope = await readEnvelope(await readJson(response));

  if (envelope.code === 0) {
    // 这里直接问 schema，而不是走契约的 `unwrapEnvelope(envelope, schema)`，两个原因：
    //
    // 1. `unwrapEnvelope` 用同一个 `null` 表示两种事：schema 拒绝了，以及解析结果
    //    本来就是 `null`。而 `DELETE /conversations/:id`、`POST /auth/logout`
    //    这类端点的成功响应**就是** `data: null`（后端约定"无内容用 null"），
    //    于是"删成功了"被判成"删失败"。
    // 2. `IgnoredDataSchema` 是手写的 `ParseableSchema`（不是 zod 类型），
    //    `unwrapEnvelope` 会把它塞进 zod 的 `envelopeSchema` 并调用 `._parse`，
    //    抛 `TypeError: keyValidator._parse is not a function`——线上删除会话
    //    报的"删除失败"就是它。异常抛在 `items.value = filter(...)` 之前，
    //    所以列表里那条会话要刷新页面才会消失。
    //
    // 结论：schema 拒绝才算结构不符；它接受（哪怕接受的是 null）就是成功。
    const parsed = schema.safeParse(envelope.data);
    if (!parsed.success) {
      throw new ApiError({
        code: ErrorCode.INTERNAL,
        message: `响应 data 不符合契约结构（${method} ${path}）`,
        httpStatus: response.status,
        requestId: envelope.requestId,
        details: undefined,
      });
    }
    return parsed.data;
  }

  if (envelope.code === null) {
    throw new ApiError({
      code: NETWORK_ERROR_CODE,
      message: `后端返回了非契约信封（HTTP ${response.status}）`,
      httpStatus: response.status,
      requestId: envelope.requestId,
      details: undefined,
    });
  }

  if (envelope.code === ErrorCode.TOKEN_EXPIRED && allowTokenRetry && auth !== false) {
    await refreshAccessToken();
    // 只重放一次；再失败就把 40101 抛给调用方，避免刷新失败时无限循环。
    return sendEnvelope(path, schema, options, false);
  }

  throw handleBusinessError(envelope, response);
}

/** 发起一次业务请求，成功返回符合契约的 `data`，失败抛 `ApiError`。 */
export function apiRequest<T>(
  path: string,
  schema: ParseableSchema<T>,
  options: ApiRequestOptions = {},
): Promise<T> {
  return sendEnvelope(path, schema, options, true);
}

/**
 * 发起请求并返回**未消费**的 `Response`（用于 SSE）。
 *
 * 与 `apiRequest` 的两点不同：
 * - 不读 body，交给调用方流式消费；
 * - **不重放 40101**。重放决策在 `chat-stream` 里做，因为只有它知道
 *   "此刻还没渲染任何内容，重放不会让用户看到重复的半句话"。
 */
export async function apiFetchRaw(path: string, options: ApiRequestOptions = {}): Promise<Response> {
  const method = options.method ?? 'POST';

  if (options.auth !== false && needsRefresh()) {
    await refreshAccessToken();
  }

  const hasBody = options.body !== undefined;
  const headers = {
    ...buildApiHeaders({ hasBody, ...(options.auth !== undefined ? { auth: options.auth } : {}) }),
    ...options.headers,
  };

  try {
    return await rawFetch(buildApiUrl(path, options.query), {
      method,
      headers,
      ...(hasBody ? { body: JSON.stringify(options.body) } : {}),
      ...(options.signal !== undefined ? { signal: options.signal } : {}),
    });
  } catch (error) {
    if (isAbortError(error)) {
      throw error;
    }
    throw networkError();
  }
}

/**
 * 给"本该是 SSE 却不是"的响应造一个 `ApiError`。
 *
 * 典型场景：`stream: false` 被服务端以 403 + 40301 拒绝，或者额度耗尽返回 42900。
 * 这些响应是普通 JSON 信封，必须按业务错误解析，而不是当成空流。
 */
export async function apiErrorFromResponse(response: Response): Promise<ApiError> {
  const envelope = await readEnvelope(await readJson(response));
  const method = 'POST';
  if (envelope.code === null || envelope.code === 0) {
    return new ApiError({
      code: NETWORK_ERROR_CODE,
      message: `后端返回了非契约信封（HTTP ${response.status}）`,
      httpStatus: response.status,
      requestId: envelope.requestId,
      details: undefined,
    });
  }
  void method;
  return handleBusinessError(envelope, response);
}
